import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Document, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()
import { ZoomableWorkspace } from 'apps/studio/components/workspace'
import { PdfPage } from 'apps/studio/components/pdfViewer/pages/PdfPage'
import { pdfFileSelector, pagesSelector } from 'apps/studio/store/selectors/pdf'
import {
  currentZoomSelector,
  shouldInitializePagesSelector,
  replacingPdfSelector,
} from 'apps/studio/store/selectors/studioSettings'
import {
  initializePdfPages,
  backFillPagesAndLayouts,
} from 'apps/studio/store/actions/sharedActions'
import { useTransformContext } from 'react-zoom-pan-pinch'
import { guidNumber } from 'apps/studio/lib/utils/entities'
import { pdfPageMetaSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import { pdfPageMetaActions } from 'apps/studio/store/reducers/pdfPageMeta'
const { addPageMeta, updatePageMeta } = pdfPageMetaActions

const PDFWorkspace = ({ file, workspaceWidth }) => {
  const [numPages, setNumPages] = useState(null)
  const [pdfLoaded, setPDFLoaded] = useState(false)
  const pdfPageMeta = useSelector(pdfPageMetaSelector)
  const currentZoom = useSelector(currentZoomSelector)
  const shouldInitializePages = useSelector(shouldInitializePagesSelector)
  const replacingPdf = useSelector(replacingPdfSelector)
  const pages = useSelector(pagesSelector)
  const pdfRef = useRef(null)
  const dispatch = useDispatch()

  useEffect(() => {
    pdfPageMeta.forEach(pageMeta => {
      const { pageNumber, initialScaleFactor, initialZoom } = pageMeta
      // Since our baseline zoom is 50% (not 100%), we normalize currentZoom by dividing it by initialZoom,
      // then multiply by the initialScaleFactor to get the updated scaleFactor for positioning calculations.
      const normalizedCurrentZoom = currentZoom / initialZoom
      dispatch(
        updatePageMeta({
          pageNumber,
          pageMeta: {
            scaleFactor: initialScaleFactor * normalizedCurrentZoom,
          },
        })
      )
    })
  }, [currentZoom])

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)

    if (shouldInitializePages) {
      // We only want to initialize pages when we first load a PDF (aka after the addPdf action fires)
      const pageGuids = Array.from({ length: numPages }, () => guidNumber())
      dispatch(initializePdfPages({ pageGuids, pageCount: numPages }))
    } else if (pages.length < numPages) {
      // This is an existing form/PDF that was created before we introduced layouts or the PDF was replaced by a new PDF with more pages.
      // Check to see if all PDF pages have a EasyFormPdfPage and EasyFormPdfLayout associated with them. If not, create them.
      const existingPageNumbers = pages.map(page => page.page_number)
      const missingPageNumbers = Array.from(
        { length: numPages },
        (_, i) => i + 1
      ).filter(pageNumber => !existingPageNumbers.includes(pageNumber))
      const pageGuids = Array.from({ length: missingPageNumbers.length }, () =>
        guidNumber()
      )
      dispatch(backFillPagesAndLayouts({ missingPageNumbers, pageGuids, replacingPdf }))
    }
  }

  const context = useTransformContext()

  const onPageLoad = index => pdfPage => {
    const {
      pageNumber,
      originalWidth,
      originalHeight,
      width,
      height,
      _pageInfo,
    } = pdfPage

    // using rotate property on the page to correctly calculate the dimensions of the page
    const rotation = _pageInfo.rotate || 0
    const isRotated = rotation === 90 || rotation === 270

    // Ensure we have set the correct dimensions for the page if rotated
    const dimensions = {
      pageNumber,
      originalWidth: isRotated ? originalHeight : originalWidth,
      originalHeight: isRotated ? originalWidth : originalHeight,
      width: isRotated ? height : width,
      height: isRotated ? width : height,
    }
    const scaleFactor =
      (workspaceWidth / dimensions.originalWidth) * currentZoom

    if (index === 0) {
      const panelElement = document.querySelector('.workspace-panel')
      const { top: panelTop } = panelElement.getBoundingClientRect()
      const { transformState } = context
      transformState.positionY = panelTop
    }

    dispatch(
      addPageMeta({
        pageMeta: {
          ...dimensions,
          initialZoom: currentZoom,
          initialScaleFactor: scaleFactor,
          scaleFactor,
          guidelineX: null,
          guidelineY: null,
        },
      })
    )
    if (!pdfLoaded && index === numPages - 1) {
      setPDFLoaded(true)
    }
  }

  return (
    <Document
      file={file}
      onLoadSuccess={onDocumentLoadSuccess}
      renderAnnotationLayer={false}
      className="pdf-document"
      inputRef={pdfRef}
    >
      {Array.from({ length: numPages }, (_, index) => (
        <PdfPage
          onLoad={onPageLoad(index)}
          pageNumber={index + 1}
          width={workspaceWidth}
          key={`pdf-page-${index}`}
        />
      ))}
    </Document>
  )
}

export const PDFViewer = () => {
  const pdfData = useSelector(pdfFileSelector)
  const [workspaceWidth, setWorkspaceWidth] = useState(0)
  const workspaceRef = useRef(null)

  // This is a fudge factor. I'm not 100% what it's correcting for,
  // but it seems to be necessary to prevent the PDF from overflowing
  // the workspace.
  const pagePadding = 4

  useEffect(() => {
    if (workspaceRef.current) {
      // TODO: This doesn't work as well as it should when things update.
      setWorkspaceWidth(workspaceRef.current.clientWidth - pagePadding)
    }
  }, [pdfData])

  return (
    <ZoomableWorkspace>
      <section className="workspace" ref={workspaceRef}>
        {/* We need to use pdfData as the key to force re-render when the PDF changes. This ensures that the onDocumentLoad success runs again when the PDF is replaced and any new pages/layouts are created, if needed */}
        <PDFWorkspace key={pdfData} file={pdfData} workspaceWidth={workspaceWidth} />
      </section>
    </ZoomableWorkspace>
  )
}
